<!-- NameSearchModal.vue -->
<template>
  <div class="modal-backdrop fade show" v-if="isOpen"></div>
  <div class="modal" tabindex="-1" :class="{ 'd-block': isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ heading }}</h5>
          <button type="button" class="btn-close" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <!-- Tabs for switching between filtered and all tickets (hidden for OTA) -->
          <ul v-if="showAllTicketsTab && !isOtaTransfer" class="nav nav-tabs mb-3">
            <li class="nav-item">
              <a
                  class="nav-link"
                  :class="{ active: activeTab === 'filtered' }"
                  href="#"
                  @click.prevent="activeTab = 'filtered'"
              >
                {{ __DICT__.nameSearch.dueOn }} {{ transferId }}
              </a>
            </li>
            <li class="nav-item">
              <a
                  class="nav-link"
                  :class="{ active: activeTab === 'all' }"
                  href="#"
                  @click.prevent="activeTab = 'all'"
              >
                {{ __DICT__.nameSearch.everyoneElse }}
              </a>
            </li>
            <li v-if="hasExternalPassengers" class="nav-item">
              <a
                  class="nav-link"
                  :class="{ active: activeTab === 'others' }"
                  href="#"
                  @click.prevent="activeTab = 'others'"
              >
                {{ __DICT__.nameSearch.others }} ({{ externalTickets.length }})
              </a>
            </li>
          </ul>

          <div class="input-group mb-3">
            <span class="input-group-text">
              <i class="ti ti-search"></i>
            </span>
            <input
                type="text"
                class="form-control"
                placeholder="Search by name or booking reference..."
                v-model="searchTerm"
            >
          </div>
          <div class="w-100 align-right text-right" style="text-align: right">{{ liftPassStatusText }}</div>

          <!-- Others Tab: External passengers for non-OTA transfers -->
          <div v-if="!isOtaTransfer && activeTab === 'others'" class="list-group" style="max-height: calc(100vh - 250px); overflow-y: auto;">
            <!-- Individual Passengers Section -->
            <div v-if="externalIndividualPassengers.length > 0" class="mb-3">
              <div class="border-bottom pb-2 mb-2">
                <strong>{{ __DICT__.individualPassengers }}</strong>
              </div>
              <button
                  v-for="ticket in externalIndividualPassengers"
                  :key="ticket.barcode"
                  class="list-group-item list-group-item-action"
                  @click="handleSelect(ticket)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-bold">
                      <i v-if="ticket.leadPassenger" class="ti ti-user-star text-warning me-1" title="Lead Passenger"></i>
                      {{ ticket.name }}
                    </div>
                    <small class="text-muted">{{ ticket.barcode }}</small>
                    <div v-if="ticket.phone" class="small text-primary">
                      <i class="ti ti-phone"></i> {{ ticket.phone }}
                    </div>
                  </div>
                  <span class="badge" :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.loaded : __DICT__.notLoaded }}
                  </span>
                </div>
              </button>
            </div>

            <!-- Grouped Passengers -->
            <div v-for="group in externalPassengerGroups" :key="group.groupId" class="mb-3">
              <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                <strong>{{ group.groupName }}</strong>
                <button
                    v-if="unloadedInGroup(group) > 0"
                    class="btn btn-sm btn-primary"
                    @click.stop="handleLoadGroup(group.groupId)"
                >
                  {{ __DICT__.nameSearch.loadAll }} ({{ unloadedInGroup(group) }})
                </button>
                <span v-else class="badge bg-success text-white">{{ __DICT__.allLoaded }}</span>
              </div>
              <button
                  v-for="ticket in group.passengers"
                  :key="ticket.barcode"
                  class="list-group-item list-group-item-action"
                  @click="handleSelect(ticket)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-bold">
                      <i v-if="ticket.leadPassenger" class="ti ti-user-star text-warning me-1" title="Lead Passenger"></i>
                      {{ ticket.name }}
                    </div>
                    <small class="text-muted">{{ ticket.barcode }}</small>
                    <div v-if="ticket.phone" class="small text-primary">
                      <i class="ti ti-phone"></i> {{ ticket.phone }}
                    </div>
                  </div>
                  <span class="badge" :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.loaded : __DICT__.notLoaded }}
                  </span>
                </div>
              </button>
            </div>

            <!-- No passengers found -->
            <div v-if="externalIndividualPassengers.length === 0 && externalPassengerGroups.length === 0" class="text-center p-3 text-muted">
              {{ __DICT__.nameSearch.noMatchingExternalPassengers }}
            </div>
          </div>

          <!-- OTA Mode: Grouped Passenger List -->
          <div v-else-if="isOtaTransfer" class="list-group" style="max-height: calc(100vh - 250px); overflow-y: auto;">
            <!-- Individual Passengers Section -->
            <div v-if="individualPassengers.length > 0" class="mb-3">
              <div class="border-bottom pb-2 mb-2">
                <strong>{{ __DICT__.individualPassengers }}</strong>
              </div>
              <button
                  v-for="ticket in individualPassengers"
                  :key="ticket.barcode"
                  class="list-group-item list-group-item-action"
                  @click="handleSelect(ticket)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-bold">
                      <i v-if="ticket.leadPassenger" class="ti ti-user-star text-warning me-1" title="Lead Passenger"></i>
                      {{ ticket.name }}
                    </div>
                    <small class="text-muted">{{ ticket.barcode }}</small>
                    <div v-if="ticket.phone" class="small text-primary">
                      <i class="ti ti-phone"></i> {{ ticket.phone }}
                    </div>
                  </div>
                  <span class="badge" :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.loaded : __DICT__.notLoaded }}
                  </span>
                </div>
              </button>
            </div>

            <!-- Grouped Passengers -->
            <div v-for="group in passengerGroups" :key="group.groupId" class="mb-3">
              <div class="d-flex justify-content-between align-items-center border-bottom pb-2 mb-2">
                <strong>{{ group.groupName }}</strong>
                <button
                    v-if="unloadedInGroup(group) > 0"
                    class="btn btn-sm btn-primary"
                    @click.stop="handleLoadGroup(group.groupId)"
                >
                  {{ __DICT__.nameSearch.loadAll }} ({{ unloadedInGroup(group) }})
                </button>
                <span v-else class="badge bg-success text-white">{{ __DICT__.allLoaded }}</span>
              </div>
              <button
                  v-for="ticket in group.passengers"
                  :key="ticket.barcode"
                  class="list-group-item list-group-item-action"
                  @click="handleSelect(ticket)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-bold">
                      <i v-if="ticket.leadPassenger" class="ti ti-user-star text-warning me-1" title="Lead Passenger"></i>
                      {{ ticket.name }}
                    </div>
                    <small class="text-muted">{{ ticket.barcode }}</small>
                    <div v-if="ticket.phone" class="small text-primary">
                      <i class="ti ti-phone"></i> {{ ticket.phone }}
                    </div>
                  </div>
                  <span class="badge" :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.loaded : __DICT__.notLoaded }}
                  </span>
                </div>
              </button>
            </div>

            <!-- No passengers found -->
            <div v-if="individualPassengers.length === 0 && passengerGroups.length === 0" class="text-center p-3 text-muted">
              {{ __DICT__.nameSearch.noMatchingPassengers }}
            </div>
          </div>

          <!-- Regular Mode: Flat Passenger List -->
          <div v-else class="list-group" style="max-height: calc(100vh - 250px); overflow-y: auto;">
            <template
                v-if="filteredTickets.length && ((activeTab === 'all' && filteredTickets.length < 200) || activeTab !== 'all')">
              <button
                  v-for="ticket in filteredTickets"
                  :key="ticket.bookingRef"
                  class="list-group-item list-group-item-action"
                  @click="handleSelect(ticket)"
              >
                <div class="d-flex justify-content-between align-items-center">
                  <div>
                    <div class="fw-bold">{{ ticket.name }}</div>
                    <small class="text-muted">{{ ticket.bookingRef }}</small>
                  </div>
                  <span
                      v-if="showEventStatus"
                      class="badge text-white"
                      :class="getStatusClass(ticket.entryStatus)"
                  >
                    {{ ticket.entryStatus.toUpperCase() }}
                  </span>
                  <span
                      v-if="showLiftPassSatus"
                      class="badge"
                      :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.yes : __DICT__.no }}
                  </span>
                  <span
                      v-if="showTransferStatus && ticket.transferTicketId"
                      class="badge"
                      :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.yes : __DICT__.no }}
                  </span>
                  <span
                      v-if="showTransferStatus && !ticket.transferTicketId"
                      class="badge bg-danger text-white">
                    {{ __DICT__.nameSearch.noTransfer }}
                  </span>
                  <span
                      v-if="showCoachStatus"
                      class="badge"
                      :class="getLiftStatusClass(ticket.loaded)">
                    {{ ticket.loaded === 1 ? __DICT__.yes : __DICT__.no }}
                  </span>
                  <span
                      v-if="showRegisteredStatus"
                      class="badge"
                      :class="getLiftStatusClass(ticket.registered)">
                    {{ ticket.registered === 1 ? __DICT__.yes : __DICT__.no }}
                  </span>
                </div>
              </button>
            </template>
            <div v-else class="text-center p-3 text-muted">
              {{ __DICT__.nameSearch.noMatchingAttendees }}
            </div>
          </div>
        </div>
      </div>
    </div>

  </div>
</template>

<script setup>
import {ref, computed} from 'vue';

const props = defineProps({
  isOpen: {
    type: Boolean,
    required: true
  },
  transferId: {
    type: String,
    default: 'this transfer'
  },
  tickets: {
    type: Array,
    required: true
  },
  heading: {
    type: String,
    required: true
  },
  showEventStatus: {
    type: Boolean,
    required: false,
    default: false
  },
  showLiftPassSatus: {
    type: Boolean,
    required: false,
    default: false
  },
  liftPassStatusText: {
    type: String,
    required: false,
    default: ""
  },
  showCoachStatus: {
    type: Boolean,
    required: false,
    default: false
  },
  showRegisteredStatus: {
    type: Boolean,
    required: false,
    default: false
  },
  showTransferStatus: {
    type: Boolean,
    required: false,
    default: false
  },
  allTickets: {
    type: Array,
    required: false,
    default: () => []
  },
  showAllTicketsTab: {
    type: Boolean,
    required: false,
    default: false
  },
  isOtaTransfer: {
    type: Boolean,
    required: false,
    default: false
  },
  groups: {
    type: Array,
    required: false,
    default: () => []
  },
  externalTickets: {
    type: Array,
    required: false,
    default: () => []
  }
});

const emit = defineEmits(['close', 'select', 'loadGroup']);

const searchTerm = ref('');
const activeTab = ref('filtered');

// Check if there are external passengers (for showing "Others" tab)
const hasExternalPassengers = computed(() => {
  return !props.isOtaTransfer && props.externalTickets && props.externalTickets.length > 0;
});

// Determine which ticket source to use based on active tab
const ticketSource = computed(() => {
  if (props.showAllTicketsTab && activeTab.value === 'all') {
    return props.allTickets || [];
  }
  return props.tickets || [];
});

const filteredTickets = computed(() => {
  const source = ticketSource.value;
  if (!searchTerm.value) return source;

  const search = searchTerm.value.toLowerCase();
  return source.filter(ticket =>
      ticket?.name?.toLowerCase().includes(search) ||
      ticket?.bookingRef?.toLowerCase().includes(search)
  );
});

// OTA Mode: Individual passengers (no groupId)
const individualPassengers = computed(() => {
  if (!props.isOtaTransfer) return [];
  const tickets = props.tickets || [];
  let individuals = tickets.filter(t => !t.groupId);

  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase();
    individuals = individuals.filter(t =>
        t?.name?.toLowerCase().includes(search) ||
        t?.bookingRef?.toLowerCase().includes(search)
    );
  }

  return individuals;
});

// OTA Mode: Grouped passengers by group
const passengerGroups = computed(() => {
  if (!props.isOtaTransfer) return [];
  const tickets = props.tickets || [];
  const groupMap = new Map();

  // Group tickets by groupId
  for (const ticket of tickets) {
    if (ticket.groupId) {
      if (!groupMap.has(ticket.groupId)) {
        groupMap.set(ticket.groupId, {
          groupId: ticket.groupId,
          groupName: ticket.groupName || `Group ${ticket.groupId}`,
          passengers: []
        });
      }
      groupMap.get(ticket.groupId).passengers.push(ticket);
    }
  }

  // Filter by search term if provided
  let groups = Array.from(groupMap.values());
  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase();
    groups = groups.map(group => ({
      ...group,
      passengers: group.passengers.filter(t =>
          t?.name?.toLowerCase().includes(search) ||
          t?.bookingRef?.toLowerCase().includes(search)
      )
    })).filter(group => group.passengers.length > 0);
  }

  return groups;
});

// Count unloaded passengers in a group (for bulk load button)
const unloadedInGroup = (group) => {
  return group.passengers.filter(p => p.loaded !== 1).length;
};

// External passengers for "Others" tab (non-OTA transfers with external passengers)
const externalIndividualPassengers = computed(() => {
  if (!hasExternalPassengers.value) return [];
  const tickets = props.externalTickets || [];
  let individuals = tickets.filter(t => !t.groupId);

  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase();
    individuals = individuals.filter(t =>
        t?.name?.toLowerCase().includes(search) ||
        t?.bookingRef?.toLowerCase().includes(search)
    );
  }

  return individuals;
});

// External grouped passengers for "Others" tab
const externalPassengerGroups = computed(() => {
  if (!hasExternalPassengers.value) return [];
  const tickets = props.externalTickets || [];
  const groupMap = new Map();

  // Group tickets by groupId
  for (const ticket of tickets) {
    if (ticket.groupId) {
      if (!groupMap.has(ticket.groupId)) {
        groupMap.set(ticket.groupId, {
          groupId: ticket.groupId,
          groupName: ticket.groupName || `Group ${ticket.groupId}`,
          passengers: []
        });
      }
      groupMap.get(ticket.groupId).passengers.push(ticket);
    }
  }

  // Filter by search term if provided
  let groups = Array.from(groupMap.values());
  if (searchTerm.value) {
    const search = searchTerm.value.toLowerCase();
    groups = groups.map(group => ({
      ...group,
      passengers: group.passengers.filter(t =>
          t?.name?.toLowerCase().includes(search) ||
          t?.bookingRef?.toLowerCase().includes(search)
      )
    })).filter(group => group.passengers.length > 0);
  }

  return groups;
});

// Handle loading all passengers in a group
const handleLoadGroup = (groupId) => {
  emit('loadGroup', groupId);
};

const getStatusClass = (status) => {
  switch (status?.toLowerCase()) {
    case 'entered':
      return 'bg-success';
    case 'none':
      return 'bg-secondary';
    default:
      return 'bg-primary';
  }
};

const getLiftStatusClass = (distributed) => {
  if (distributed) {
    return 'bg-success text-white'
  } else {
    return 'bg-primary text-white'
  }
}

const handleSelect = (ticket) => {
  emit('select', ticket.barcode);
  emit('close');
};
</script>

<style scoped>
.modal .modal-dialog .modal-content {
  height: calc(100vh - 30px);
}
</style>